import { Box } from '@mui/material';
import { useDeleteChallengeById } from '_api/mutations/challenge';
import { Challenge } from '_api/types';
import { ChallengeRouteMap } from '_constants';
import { ThumbnailView } from 'components/atoms';
import { ActionsFlexBox } from 'components/molecules';
import { ViewButtons } from 'components/molecules/types';
import { TableHeader } from 'components/organisms';
import { AddChallengeToPatientModal } from 'components/organisms/modals';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigateTo } from 'utils';
import { useChallenges, useSearchResults, useUser } from 'utils/hooks';

export const ChallengeManagement = () => {
  const { existingSearchTerm, updateSearchTerm } = useSearchResults();
  const navigate = useNavigateTo();
  const toChallengeFormView = () => navigate(ChallengeRouteMap.ADDFORM);
  const { allChallenges, isLoading } = useChallenges();
  const { isSuperAdmin, canEditObject } = useUser();
  const { mutateAsync: deleteChallenge } = useDeleteChallengeById();

  const [id, setId] = useState(0);
  const [challenge, setChallenge] = useState<Challenge | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState(existingSearchTerm || '');
  const [filteredData, setFilteredData] = useState<Challenge[]>([]);

  useEffect(() => setSearchTerm(existingSearchTerm || ''), [existingSearchTerm]);

  useEffect(() => {
    if (allChallenges) {
      setFilteredData(allChallenges);
    }
  }, [allChallenges]);

  useMemo(() => {
    if (allChallenges) {
      const lowercasedSearchTerm = searchTerm.toLowerCase();
      const filtered = allChallenges.filter((challenge) => {
        const title = challenge.title.toLowerCase();
        return title.includes(lowercasedSearchTerm);
      });
      setFilteredData(filtered);
    }
  }, [searchTerm, allChallenges]);

  const handleViewOnClick = (id: number) => {
    navigate(ChallengeRouteMap.DETAIL, id.toString());
  };

  const handleEditOnClick = (id: number) => {
    navigate(ChallengeRouteMap.EDIT, id.toString());
  };

  const handleDeleteOnClick = (id: number) => {
    deleteChallenge({ id });
  };

  const handleAddOnClick = (id: number, ch: Challenge) => {
    setId(id);
    setChallenge(ch);
  };

  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
    updateSearchTerm(searchTerm);
  };

  const actionButtons: ViewButtons = {
    viewButton: true,
    editButton: true,
    deleteButton: true,
    addButton: true,
  };

  const tableColumns = useMemo<MRT_ColumnDef<Challenge>[]>(
    () => [
      {
        header: 'Exercise',
        enableSorting: false,
        Cell: ({ row }) => (
          <ThumbnailView fullImageUrl={row.original.fullImageUrl} rowView={true} />
        ),
        size: 280,
      },
      {
        header: 'Title',
        accessorFn: (row) => row.title,
        size: 280,
      },
      {
        header: 'Actions',
        enableSorting: false,
        accessorFn: (row) => (
          <ActionsFlexBox
            msg={'challenge'}
            viewOnClick={() => handleViewOnClick(row.id)}
            editOnClick={() => handleEditOnClick(row.id)}
            deleteOnClick={() => handleDeleteOnClick(row.id)}
            addOnClick={() => handleAddOnClick(row.id, row)}
            actionButtons={actionButtons}
            disableEdit={!isSuperAdmin}
            disableDeleteTooltip={
              canEditObject(row)
                ? undefined
                : 'Cannot delete challenges you did not create'
            }
          />
        ),
        size: 10,
      },
    ],
    [],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <TableHeader
          onAddClick={toChallengeFormView}
          disableButton={!isSuperAdmin}
          tooltip={'You cannot create challenges'}
          handleSearch={handleSearch}
          value={searchTerm}
        />
      </Box>
      <Box sx={{ marginTop: 10 }}>
        {filteredData && !isLoading ? (
          <MaterialReactTable
            columns={tableColumns}
            data={filteredData}
            enableSorting={true}
            enableColumnActions={false}
            enableTopToolbar={false}
            enablePagination={true}
          />
        ) : null}
      </Box>
      <AddChallengeToPatientModal
        content={challenge as Challenge}
        open={!!challenge}
        onClose={() => setChallenge(undefined)}
        id={id}
      />
    </Box>
  );
};
